import { Component, type ErrorInfo, type ReactNode } from 'react';
import { CircleAlert, RefreshCw } from 'lucide-react';
import { errorMessage } from '@/lib/graphql';
import { Button } from '@/components/ui/button';

interface State {
  error: Error | null;
}

/**
 * Última red de seguridad. Un fallo de datos ya lo cubre `ErrorState` en cada
 * vista; esto es para el error de render inesperado, que sin barrera dejaría la
 * pantalla en blanco y sin forma de salir.
 */
export class RouteErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sin servicio de telemetría todavía: la consola es el único destino útil.
    console.error('Fallo no controlado en una ruta', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-canvas px-6 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-danger-subtle text-danger-subtle-fg">
          <CircleAlert className="size-7" aria-hidden="true" />
        </div>
        <div className="max-w-prose">
          <h1 className="text-lg font-semibold text-fg">La pantalla no se pudo mostrar</h1>
          <p className="mt-1 text-sm text-fg-muted">{errorMessage(error)}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => this.setState({ error: null })}>
            <RefreshCw aria-hidden="true" />
            Reintentar
          </Button>
          <Button variant="primary" onClick={() => window.location.assign('/')}>
            Ir al tablero
          </Button>
        </div>
      </div>
    );
  }
}
